import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button, Input, Alert, Card, CardBody } from '../components/ui';
import { ArbieMascot } from '../components/ArbieMascot';
import { useAuth } from '../hooks/useAuth';
import { extractApiErrorMessage } from '../api/api-error';

const BASE_URL = import.meta.env.VITE_API_URL || '';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useMagicLink, setUseMagicLink] = useState(true);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkUrl, setMagicLinkUrl] = useState<string | null>(null);

  // Get the redirect path from location state, default to dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, from]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!email.trim()) {
        setError('Email is required');
        return;
      }

      setIsLoading(true);

      try {
        if (useMagicLink) {
          // Request magic link
          const response = await fetch(`${BASE_URL}/api/auth/magic-link/request`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(extractApiErrorMessage(errorData, 'Failed to send magic link'));
          }

          const data = await response.json();
          setMagicLinkSent(true);

          // In development, show the magic link
          if (data.magicLink) {
            setMagicLinkUrl(data.magicLink);
          }
        } else {
          // Traditional password login
          if (!password) {
            setError('Password is required');
            return;
          }

          await login(email, password, rememberMe);
          navigate(from, { replace: true });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [email, password, rememberMe, useMagicLink, login, navigate, from]
  );

  // Show nothing while checking initial auth state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ArbieMascot size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo and Title */}
        <div className="flex flex-col items-center">
          <ArbieMascot size="lg" className="mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Recruiter Buddy</h1>
          <h2 className="mt-2 text-center text-lg text-gray-600">Sign in to your account</h2>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card variant="elevated">
          <CardBody className="py-8 px-6 sm:px-10">
            {magicLinkSent ? (
              <div className="space-y-4">
                {/* Success Message */}
                <Alert variant="success">
                  Check your email for a login link. The link will expire in 15 minutes.
                </Alert>

                {/* Development only: Show magic link */}
                {magicLinkUrl && (
                  <Alert variant="info">
                    <div className="space-y-2">
                      <p className="font-semibold">Development Mode:</p>
                      <a
                        href={magicLinkUrl}
                        className="text-blue-600 hover:text-blue-800 underline break-all"
                      >
                        Click here to login
                      </a>
                    </div>
                  </Alert>
                )}

                {/* Back to Login */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setMagicLinkSent(false);
                    setMagicLinkUrl(null);
                    setError(null);
                  }}
                  fullWidth
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Alert */}
                {error && (
                  <Alert variant="error" dismissible onDismiss={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                {/* Login Method Toggle */}
                <div className="flex items-center justify-center space-x-4 pb-4 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => setUseMagicLink(false)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      !useMagicLink
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    disabled={isLoading}
                  >
                    Password
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseMagicLink(true)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      useMagicLink
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    disabled={isLoading}
                  >
                    Email Link
                  </button>
                </div>

                {/* Email Input */}
                <Input
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                  disabled={isLoading}
                  fullWidth
                />

                {!useMagicLink && (
                  <>
                    {/* Password Input */}
                    <Input
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      disabled={isLoading}
                      fullWidth
                    />

                    {/* Remember Me Checkbox */}
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        disabled={isLoading}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                      />
                      <label
                        htmlFor="remember-me"
                        className="ml-2 block text-sm text-gray-700 cursor-pointer select-none"
                      >
                        Remember me for 30 days
                      </label>
                    </div>
                  </>
                )}

                {useMagicLink && (
                  <p className="text-sm text-gray-600">
                    We'll send you a secure login link that expires in 15 minutes.
                  </p>
                )}

                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-500"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit Button */}
                <Button type="submit" loading={isLoading} fullWidth size="lg">
                  {useMagicLink ? 'Send Login Link' : 'Sign In'}
                </Button>
              </form>
            )}
          </CardBody>
        </Card>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-gray-500">
          AI-powered recruitment automation
        </p>
      </div>
    </div>
  );
};

export default Login;
